import {GroundworksTableData, TableCell, TableColumn, TableRow} from '@groundworks/ngx-groundworks-ui';
import {BoundProductInfo, TaxInfo} from '../sdk/sdk';

const PAR_COLUMN: TableColumn = {label: 'PAR', dataType: 'currency'};
const TOTAL_CELL_CLASS = 'fw-bold bg-light';

export function buildProductTableData(
  productColumnLabel: string,
  products: BoundProductInfo[],
  grossTotal: number,
  netTotal: number,
  showParPrice = false,
  taxes: TaxInfo[] = []
): GroundworksTableData {
  const rows: TableRow[] = [
    ...products.map((p) => buildProductRow(p, showParPrice)),
    buildTotalRow(products, grossTotal, netTotal, showParPrice),
    ...taxes.map((t) => buildTaxRow(t, showParPrice)),
  ];
  return {columns: buildColumns(productColumnLabel, showParPrice), groups: [{label: '', rows}]};
}

// PAR trails Net (MOD-4539 option B). GroundworksTableComponent does not check `cells` against
// `columns`, so a column moved here without moving the matching cell in ALL of buildProductRow,
// buildTotalRow and buildTaxRow silently renders values under the wrong headers.
function buildColumns(productColumnLabel: string, showParPrice: boolean): TableColumn[] {
  return [
    {label: productColumnLabel, dataType: 'text'},
    {label: 'Units', dataType: 'text'},
    {label: 'Gross', dataType: 'currency'},
    {label: 'Net', dataType: 'currency'},
    ...(showParPrice ? [PAR_COLUMN] : []),
  ];
}

function buildProductRow(product: BoundProductInfo, showParPrice: boolean): TableRow {
  return {
    type: 'data',
    cells: [
      buildNameCell(product),
      {value: `${product.quantity ?? 0} ${product.productInfo?.unitOfMeasure ?? 'Each'}`},
      {value: product.grossAmount ?? 0},
      {value: product.netAmount ?? 0},
      ...(showParPrice ? [buildParCell(product)] : []),
    ],
  };
}

// The column shows the extended value because that is what the total sums. Showing the per-unit
// rate as the primary value made the total not the sum of its own column — ten units at $10 read
// $10 in the row and $100 in the total. The rate is still visible, as sub-text.
function buildParCell(product: BoundProductInfo): TableCell {
  return {
    value: extendedParPrice(product),
    subText: `${formatCurrency(product.parPrice ?? 0)} / unit`,
  };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(value);
}

function buildNameCell(product: BoundProductInfo): TableCell {
  const value = product.productInfo?.name ?? '—';
  const note = product.note?.trim();
  if (!note) return {value};
  return {
    value,
    icon: {
      class: 'bi bi-sticky text-primary',
      tooltip: note,
      ariaLabel: `Notes for ${product.productInfo?.name ?? 'product'}`,
      position: 'after',
      tooltipPlacement: 'top',
    },
  };
}

function buildTotalRow(
  products: BoundProductInfo[],
  grossTotal: number,
  netTotal: number,
  showParPrice: boolean
): TableRow {
  return {
    type: 'total',
    cells: [
      {value: 'Products Total', isHeader: true, colspan: 2, className: TOTAL_CELL_CLASS},
      buildTotalCell(grossTotal),
      buildTotalCell(netTotal),
      ...(showParPrice ? [buildTotalCell(sumParPrice(products))] : []),
    ],
  };
}

function buildTotalCell(value: number): TableCell {
  return {value, isHeader: true, className: TOTAL_CELL_CLASS};
}

// Tax sits BELOW the Products Total row, not above it: a job tax is billed off the project but is
// not one of its products, and the total this table renders is a products subtotal (the server
// computes ProjectGrossAmount/ProjectNetAmount as a pure bound-product sum, with no tax term). A
// row placed after the total cannot be read as one of its addends.
//
// The amount goes under Net alone — a tax has one figure, not a gross/net pair — which is why the
// label spans the first three columns and PAR is padded with a blank cell.
function buildTaxRow(tax: TaxInfo, showParPrice: boolean): TableRow {
  return {
    type: 'data',
    cells: [
      {value: tax.taxTypeName ?? 'Tax', colspan: 3},
      {value: tax.amount ?? 0},
      ...(showParPrice ? [{value: ''}] : []),
    ],
  };
}

// parPrice is a benchmark price *per unit*, so the total is price x quantity. It shares
// extendedParPrice with the row cell deliberately: the total is the sum of the same quantity the
// column renders, by construction rather than by two expressions that happen to agree.
//
// Do NOT "fix" this into a totalParPrice parameter to match grossTotal/netTotal. Those are
// parameters because a server-authoritative total may exist and must win over a client-side sum —
// see project.component.ts, which prefers project.projectGrossAmount and only falls back to
// products.reduce(...). There is no projectParAmount anywhere, so PAR has no authoritative source
// to defer to. The pattern is "pass it in when an authoritative value exists", not "always pass
// totals in", so summing here is consistent with the reason for the pattern despite differing in
// shape.
//
// The arithmetic itself is not business-confirmed — MOD-4539 records it as needing sign-off.
function sumParPrice(products: BoundProductInfo[]): number {
  return products.reduce((total, p) => total + extendedParPrice(p), 0);
}

// A missing parPrice or quantity is 0, never NaN — both are optional on BoundProductInfo.
function extendedParPrice(product: BoundProductInfo): number {
  return (product.parPrice ?? 0) * (product.quantity ?? 0);
}
